import React, {useCallback, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import throttle from 'lodash/throttle'
import {Spacing} from 'types/Theme'

import {useElementSize} from '@vio/core/src/_web/utils/browser/hooks/useElementSize'
import {getIsRtlLanguage} from '@vio/core/src/auth/modules/selectors'

import {cssTheme} from '../../../../atlas-tokens'
import {ScrollButton, SwiperButtonSizeType} from './ScrollButton'

const SwiperContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  position: relative;
`
const SwiperWrapper = styled.div<{gap: Spacing}>`
  display: flex;
  width: 100%;
  overflow: scroll;
  /* hide scrollbar */
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }

  gap: ${({gap}) => cssTheme.layout.spacing[gap]};
`

const THROTTLING_INTERVAL = 500 // ms

type ActionType = 'NEXT' | 'PREV'

interface Props {
  // Elements that will be rendered inside the scroll area as slides
  children: React.ReactNode
  // Gap between slides
  gap: Spacing
  // Left margin for the left arrow button
  left?: number
  // Right margin for the right arrow button
  right?: number
  // Threshold for scroll position
  threshold?: number
  // Shadow width
  shadowWidth?: number
  // Button size
  buttonSize?: SwiperButtonSizeType
}

export const Swiper: React.FC<Props> = ({
  children,
  gap,
  left = 0,
  right = 0,
  threshold = 200,
  shadowWidth = 120,
  buttonSize = 'sm'
}) => {
  const isRtl = useSelector(getIsRtlLanguage)

  const {
    elementRef,
    size: {width: wrapperWidth, el: wrapperElement}
  } = useElementSize<HTMLDivElement>()

  const [scrollableOffset, setScrollableOffset] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [scrollWidth, setScrollWidth] = useState(0)

  useEffect(() => {
    if (wrapperElement) {
      const roundedWrapperWidth = Math.ceil(wrapperWidth)
      const newScrollableOffset =
        roundedWrapperWidth > 0
          ? Math.ceil(wrapperElement.scrollWidth) - roundedWrapperWidth
          : 0

      if (scrollableOffset !== newScrollableOffset) {
        setScrollableOffset(Math.round(newScrollableOffset))
      }
    }
  }, [scrollableOffset, wrapperWidth, scrollPosition, wrapperElement])

  useEffect(() => {
    const ref = wrapperElement

    if (ref) {
      setScrollWidth(Math.ceil(ref.scrollWidth))

      const onScroll = throttle(() => {
        setScrollPosition(Math.round(ref.scrollLeft))
      }, THROTTLING_INTERVAL)

      ref.addEventListener('scroll', onScroll)
      return () => ref.removeEventListener('scroll', onScroll)
    }
  }, [wrapperElement])

  const getWidthToScroll = useCallback(
    (direction: ActionType) => {
      const realScrollPosition = isRtl ? -scrollPosition : scrollPosition
      if (direction === 'NEXT') {
        if (scrollWidth >= realScrollPosition + wrapperWidth)
          return Math.ceil(realScrollPosition + wrapperWidth) - threshold
        return scrollableOffset
      }
      if (realScrollPosition + threshold >= wrapperWidth)
        return Math.ceil(realScrollPosition - wrapperWidth + threshold)
      return 0
    },
    [
      isRtl,
      scrollPosition,
      scrollWidth,
      scrollableOffset,
      wrapperWidth,
      threshold
    ]
  )

  const isButtonVisible = useCallback(
    (direction: ActionType): boolean => {
      if (isRtl) {
        if (direction === 'NEXT') return -scrollPosition < scrollableOffset
        return scrollPosition < 0
      }

      if (direction === 'NEXT') return scrollPosition < scrollableOffset
      return scrollPosition > 0
    },
    [isRtl, scrollPosition, scrollableOffset]
  )

  const onArrowClick = useCallback(
    (direction: ActionType) => {
      if (!isButtonVisible(direction)) return
      const widthToScroll = getWidthToScroll(direction)
      wrapperElement?.scroll({
        left: (isRtl ? -1 : 1) * widthToScroll,
        behavior: 'smooth'
      })
    },
    [wrapperElement, getWidthToScroll, isRtl, isButtonVisible]
  )

  return (
    <SwiperContainer>
      <SwiperWrapper gap={gap} ref={elementRef}>
        {children}
      </SwiperWrapper>
      <ScrollButton
        direction="left"
        isVisible={isButtonVisible('PREV')}
        onClick={() => onArrowClick('PREV')}
        marginLeft="s000"
        paddingLeft="s300"
        shadowWidth={shadowWidth}
        left={left}
        isRtl={isRtl}
        size={buttonSize}
      />
      <ScrollButton
        direction="right"
        isVisible={isButtonVisible('NEXT')}
        onClick={() => onArrowClick('NEXT')}
        marginRight="s000"
        paddingRight="s300"
        shadowWidth={shadowWidth}
        right={right}
        isRtl={isRtl}
        size={buttonSize}
      />
    </SwiperContainer>
  )
}
